import * as XLSX from 'xlsx';

interface ExportData {
  uniqueDomain?: string;
  country?: string;
  fte?: number | string;
  iabDomainCategory?: string;
  summaryDescription?: string;
  buyAndBuildTargetRelevance?: number;
  manuallyLabeledScore?: number;
  validated?: boolean;
  pipedriveId?: number;
  productCategorization?: {
    edges?: Array<{
      node?: {
        softwareCategory?: {
          subCategory1?: string;
          subCategory2?: string;
          subCategory3?: string;
        };
        softwareCategoryCertainty?: number;
      };
    }>;
  };
  latestCrawlResult?: {
    xoviKeywords?: {
      edges?: Array<{
        node?: {
          keyword?: string;
        };
      }>;
    };
  };
}

/**
 * Formats data for Excel export by extracting and flattening relevant fields
 */
const formatDataForExport = (data: ExportData[]) => {
  return data.map(row => {
    const domain = row.uniqueDomain || '';
    const pipedriveUrl = row.pipedriveId ? `https://bid.pipedrive.com/organization/${row.pipedriveId}` : '';

    // Extract product categories
    let productCategory = '';
    if (row.productCategorization?.edges && row.productCategorization.edges.length > 0) {
      const sorted = [...row.productCategorization.edges].sort(
        (a, b) => (b?.node?.softwareCategoryCertainty || 0) - (a?.node?.softwareCategoryCertainty || 0)
      );

      const category = sorted[0]?.node?.softwareCategory;
      if (category) {
        const subcategories = [category.subCategory1, category.subCategory2, category.subCategory3].filter(Boolean);

        productCategory = subcategories.join(', ');
      }
    }

    return {
      Domain: domain,
      Verified: row.validated ? 'Yes' : 'No',
      Country: row.country || '',
      FTE: row.fte || '',
      Product: productCategory || '',
      Keywords:
        row.latestCrawlResult?.xoviKeywords?.edges
          ?.slice(0, 5)
          .map(edge => edge?.node?.keyword)
          .filter(Boolean)
          .join(', ') || '',
      Summary: row.summaryDescription || '',
      'Relevance Score': row.manuallyLabeledScore || row.buyAndBuildTargetRelevance || '',
      'Pipedrive Link': pipedriveUrl,
    };
  });
};

/**
 * Adds hyperlinks to a worksheet
 */
const addHyperlinksToSheet = (sheet: XLSX.WorkSheet, data: ReturnType<typeof formatDataForExport>) => {
  data.forEach((row, rowIndex) => {
    const excelRow = rowIndex + 2; // +1 for header, +1 for 0-index

    // Make Domain column clickable (column A)
    if (row.Domain) {
      const domainCell = `A${excelRow}`;
      const domainUrl = row.Domain.startsWith('http') ? row.Domain : `https://${row.Domain}`;
      sheet[domainCell].l = { Target: domainUrl };
    }

    // Make Pipedrive Link clickable (column I)
    if (row['Pipedrive Link']) {
      const pipedriveCell = `I${excelRow}`;
      if (sheet[pipedriveCell]) {
        sheet[pipedriveCell].l = { Target: row['Pipedrive Link'] };
      }
    }
  });
};

/**
 * Exports Buy & Build data to Excel file with two tabs: Longlist and Shortlist
 */
export const exportBuyAndBuildToExcel = (
  longlistData: ExportData[],
  shortlistData: ExportData[],
  companyName: string
) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Format data for both tabs
  const formattedLonglist = formatDataForExport(longlistData);
  const formattedShortlist = formatDataForExport(shortlistData);

  // Create worksheets
  const longlistSheet = XLSX.utils.json_to_sheet(formattedLonglist);
  const shortlistSheet = XLSX.utils.json_to_sheet(formattedShortlist);

  // Add hyperlinks to both sheets
  addHyperlinksToSheet(longlistSheet, formattedLonglist);
  addHyperlinksToSheet(shortlistSheet, formattedShortlist);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 30 }, // Domain
    { wch: 10 }, // Verified
    { wch: 10 }, // Country
    { wch: 10 }, // FTE
    { wch: 25 }, // Product
    { wch: 40 }, // Keywords
    { wch: 50 }, // Summary
    { wch: 15 }, // Relevance Score
    { wch: 50 }, // Pipedrive Link
  ];

  longlistSheet['!cols'] = columnWidths;
  shortlistSheet['!cols'] = columnWidths;

  // Add worksheets to workbook
  XLSX.utils.book_append_sheet(workbook, longlistSheet, 'Longlist');
  XLSX.utils.book_append_sheet(workbook, shortlistSheet, 'Shortlist');

  // Generate filename with company name and timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const sanitizedCompanyName = companyName.replace(/[^a-z0-9]/gi, '_');
  const filename = `BuyBuild_${sanitizedCompanyName}_${timestamp}.xlsx`;

  // Write and download the file
  XLSX.writeFile(workbook, filename);
};
